import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Popable } from "react-native-popable";
import { useReactiveVar } from "react-reactive-var";
import { userNotifications } from "../../../App";

dayjs.extend(relativeTime);

const HIDE_DELAY = 150;

const NotificationBell = () => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const notifications = useReactiveVar(userNotifications);
  const [open, setOpen] = useState(false);
  const hideTimeout = useRef(null);
  const { width } = useWindowDimensions();

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n?._read).length,
    [notifications],
  );
  const hasNotifications = (notifications || []).length > 0;

  const showPanel = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    setOpen(true);
  };

  const hidePanel = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
    hideTimeout.current = setTimeout(() => {
      setOpen(false);
      hideTimeout.current = null;
    }, HIDE_DELAY);
  };

  useEffect(() => {
    return () => {
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
      }
    };
  }, []);

  const handlePress = (item) => {
    try {
      const meta =
        typeof item?.metadata === "string"
          ? JSON.parse(item.metadata)
          : item?.metadata || {};
      if (meta?.chatId) {
        const wsId = item?.workspaceID;
        if (wsId && typeof window !== "undefined" && window.location) {
          window.location.assign(`/workspaces/${wsId}/chats/${meta.chatId}`);
        }
      }
    } catch (e) {
      console.error("Notification nav failed:", e);
    }
    setOpen(false);
  };

  const markAllRead = () => {
    userNotifications(
      (notifications || []).map((n) => ({ ...n, _read: true })),
    );
  };

  return (
    <Popable
      backgroundColor={currentTheme?.theme?.cardBackground}
      caret={false}
      visible={open}
      position="bottom"
      strictPosition
      style={{
        top: 34,
        right: 0,
        zIndex: 30,
        width: 360,
        maxWidth: Math.max(260, width - 48),
        transform: [{ translateX: 0 }, { translateY: 0 }],
      }}
      content={
        <View
          onMouseEnter={showPanel}
          onMouseLeave={hidePanel}
          style={{
            width: "100%",
            maxHeight: 400,
            backgroundColor: currentTheme?.theme?.cardBackground,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: currentTheme?.theme?.border_color,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderBottomWidth: 1,
              borderBottomColor: currentTheme?.theme?.border_color,
            }}
          >
            <AppText
              fontWeight="bold"
              style={{ color: currentTheme?.theme?.font_color }}
            >
              {t("topbar.notifications.title")}
            </AppText>
            {unreadCount > 0 && (
              <TouchableOpacity onPress={markAllRead}>
                <AppText
                  style={{
                    color: currentTheme?.theme?.primary,
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                  }}
                >
                  {t("topbar.notifications.markAllRead")}
                </AppText>
              </TouchableOpacity>
            )}
          </View>
          <ScrollView style={{ maxHeight: 320 }}>
            {!hasNotifications ? (
              <View
                style={{
                  paddingHorizontal: 24,
                  paddingVertical: 28,
                  alignItems: "center",
                }}
              >
                <AppIcon
                  icon={AppAssets.icons.notification}
                  size={22}
                  color={currentTheme?.theme?.darkGrey}
                  wrapperStyle={{ marginBottom: 10 }}
                />
                <AppText
                  fontWeight="bold"
                  style={{
                    color: currentTheme?.theme?.font_color,
                    marginBottom: 8,
                  }}
                >
                  {t("topbar.notifications.emptyTitle")}
                </AppText>
                <AppText
                  style={{
                    color: currentTheme?.theme?.darkGrey,
                    fontSize: currentTheme?.theme?.semi_small_font_size,
                    textAlign: "center",
                    lineHeight: 18,
                  }}
                >
                  {t("topbar.notifications.emptyDesc")}
                </AppText>
              </View>
            ) : (
              <>
                <View
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    backgroundColor: currentTheme?.theme?.softBackgroundColor,
                  }}
                >
                  <AppText
                    style={{
                      color: currentTheme?.theme?.mediumGrey,
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                    }}
                  >
                    {t("topbar.notifications.populatedDesc")}
                  </AppText>
                </View>
                {(notifications || []).map((n) => (
                  <TouchableOpacity
                    key={n?.id}
                    onPress={() => handlePress(n)}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 10,
                      borderBottomWidth: 1,
                      borderBottomColor: currentTheme?.theme?.border_color,
                      backgroundColor: n?._read
                        ? "transparent"
                        : currentTheme?.theme?.softBackgroundColor,
                    }}
                  >
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      {!n?._read && (
                        <View
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: currentTheme?.theme?.primary,
                            marginRight: 8,
                          }}
                        />
                      )}
                      <AppText
                        fontWeight="bold"
                        style={{
                          color: currentTheme?.theme?.font_color,
                          flex: 1,
                        }}
                        numberOfLines={1}
                      >
                        {n?.title || t("topbar.notifications.item")}
                      </AppText>
                    </View>
                    {n?.description ? (
                      <AppText
                        style={{
                          color: currentTheme?.theme?.darkGrey,
                          fontSize: currentTheme?.theme?.semi_small_font_size,
                          marginTop: 2,
                        }}
                        numberOfLines={2}
                      >
                        {n?.description}
                      </AppText>
                    ) : null}
                    {n?.createdAt ? (
                      <AppText
                        style={{
                          color: currentTheme?.theme?.mediumGrey,
                          fontSize: currentTheme?.theme?.extra_small_font_size,
                          marginTop: 2,
                        }}
                      >
                        {dayjs(n.createdAt).fromNow()}
                      </AppText>
                    ) : null}
                  </TouchableOpacity>
                ))}
              </>
            )}
          </ScrollView>
        </View>
      }
    >
      <View
        style={{ position: "relative", marginRight: 5 }}
        onMouseEnter={showPanel}
        onMouseLeave={hidePanel}
      >
        <AppIcon
          isClickable
          icon={AppAssets.icons.notification}
          width={"100%"}
          size={16}
          wrapperStyle={{ height: 24, width: 24, marginTop: 5 }}
          style={{ height: 22, width: 22 }}
        />
        {unreadCount > 0 && (
          <View
            style={{
              position: "absolute",
              top: 0,
              right: -2,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              paddingHorizontal: 4,
              backgroundColor: currentTheme?.theme?.red || "#e53935",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppText
              style={{
                color: "#fff",
                fontSize: 10,
                fontWeight: "bold",
              }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </AppText>
          </View>
        )}
      </View>
    </Popable>
  );
};

export default NotificationBell;
